import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import {
  RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Target,
  AlertCircle,
  CheckCircle,
  Brain,
  Activity
} from 'lucide-react';
import { 
  PerfectShotOptimizer, 
  PlayerProfile, 
  OptimizationResult 
} from '../utils/perfectShotOptimizer';
import type { ShotData } from '../types';
import { cn } from '@/lib/utils';

interface PerfectShotOptimizerUIProps {
  currentShot?: ShotData;
  historicalShots?: ShotData[];
  playerProfile?: PlayerProfile;
  className?: string;
  onOptimizationComplete?: (result: OptimizationResult) => void;
}

export const PerfectShotOptimizerUI: React.FC<PerfectShotOptimizerUIProps> = ({
  currentShot,
  historicalShots = [],
  playerProfile,
  className,
  onOptimizationComplete
}) => {
  const [optimizer] = useState(() => new PerfectShotOptimizer(playerProfile));
  const [optimizationResult, setOptimizationResult] = useState<OptimizationResult | null>(null);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [calibrationComplete, setCalibrationComplete] = useState(false);
  const [realTimeMode, setRealTimeMode] = useState(false);
  const [selectedShotType, setSelectedShotType] = useState('drive');
  
  // Simulated real-time values for demo
  const [realTimeBatSpeed, setRealTimeBatSpeed] = useState(30);
  const [realTimeAngle, setRealTimeAngle] = useState(25);
  const [timeToImpact, setTimeToImpact] = useState(500);

  // Initialize optimizer with data
  useEffect(() => {
    if (playerProfile) {
      optimizer.setPlayerProfile(playerProfile);
    }
    if (historicalShots.length > 0) {
      optimizer.addHistoricalShots(historicalShots);
    }
  }, [playerProfile, historicalShots, optimizer]);

  // Optimize current shot
  useEffect(() => {
    if (currentShot) {
      const result = optimizer.optimizeShot(currentShot);
      setOptimizationResult(result);
      if (onOptimizationComplete) {
        onOptimizationComplete(result);
      }
    }
  }, [currentShot, optimizer, onOptimizationComplete]);

  // Calibrate optimizer
  const handleCalibration = () => {
    if (historicalShots.length < 10) {
      return;
    }
    
    setIsCalibrating(true);
    
    // Simulate calibration process
    setTimeout(() => {
      try {
        optimizer.calibrateForPlayer(historicalShots);
        setCalibrationComplete(true);
      } catch (error) {
        console.error('Calibration error:', error);
      } finally {
        setIsCalibrating(false);
      }
    }, 2000);
  };

  // Real-time optimization
  const realTimeOptimization = useMemo(() => {
    if (!realTimeMode) return null;
    
    return optimizer.realTimeOptimization(
      realTimeBatSpeed,
      realTimeAngle,
      timeToImpact
    );
  }, [realTimeMode, realTimeBatSpeed, realTimeAngle, timeToImpact, optimizer]);

  // Calculate optimal parameters for selected shot type
  const optimalParameters = useMemo(() => {
    return optimizer.calculateOptimalParameters(selectedShotType);
  }, [selectedShotType, optimizer]);

  // Prepare visualization data
  const improvementRadarData = useMemo(() => {
    if (!optimizationResult) return [];
    
    return [
      { metric: 'Bat Speed', current: 70, optimal: 100 },
      { metric: 'Contact', current: 60, optimal: 95 },
      { metric: 'Timing', current: 80, optimal: 90 },
      { metric: 'Angle', current: 75, optimal: 85 },
      { metric: 'Power', current: 65, optimal: 90 },
      { metric: 'Consistency', current: 70, optimal: 85 }
    ];
  }, [optimizationResult]);

  const potentialGainData = useMemo(() => {
    if (!optimizationResult) return [];
    
    return [
      { 
        name: 'Distance', 
        gain: optimizationResult.potentialGain.distance,
        unit: 'm'
      },
      { 
        name: 'Power', 
        gain: optimizationResult.potentialGain.power,
        unit: '%'
      },
      { 
        name: 'Consistency', 
        gain: optimizationResult.potentialGain.consistency,
        unit: '%'
      }
    ];
  }, [optimizationResult]);

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6" />
            Perfect Shot Optimizer
          </h2>
          <p className="text-muted-foreground">
            AI-powered shot optimization and real-time coaching
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCalibration}
            disabled={isCalibrating || historicalShots.length < 10}
          >
            {isCalibrating ? 'Calibrating...' : 'Calibrate'}
          </Button>
          <Button
            variant={realTimeMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => setRealTimeMode(!realTimeMode)}
          >
            <Activity className="h-4 w-4 mr-2" />
            {realTimeMode ? 'Exit Real-Time' : 'Real-Time Mode'}
          </Button>
        </div>
      </div>

      {/* Calibration Status */}
      {calibrationComplete && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Optimizer calibrated with {historicalShots.length} historical shots
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="analysis" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="analysis">Shot Analysis</TabsTrigger>
          <TabsTrigger value="optimization">Optimization</TabsTrigger>
          <TabsTrigger value="realtime">Real-Time</TabsTrigger>
        </TabsList>

        {/* Shot Analysis Tab */}
        <TabsContent value="analysis" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Current vs Optimal Radar */}
            <Card>
              <CardHeader>
                <CardTitle>Performance Comparison</CardTitle>
                <CardDescription>Current vs Optimal Parameters</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={improvementRadarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar
                      name="Current"
                      dataKey="current"
                      stroke="#f59e0b"
                      fill="#f59e0b"
                      fillOpacity={0.3}
                    />
                    <Radar
                      name="Optimal"
                      dataKey="optimal"
                      stroke="#10b981"
                      fill="#10b981"
                      fillOpacity={0.3}
                    />
                    <Legend />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Potential Gains */}
            <Card>
              <CardHeader>
                <CardTitle>Potential Gains</CardTitle>
                <CardDescription>Achievable improvements</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={potentialGainData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="gain" fill="#3b82f6">
                      {potentialGainData.map((entry, index) => (
                        <Bar key={`cell-${index}`} fill={entry.gain > 0 ? '#10b981' : '#ef4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Improvement Recommendations */}
          {optimizationResult && (
            <Card>
              <CardHeader>
                <CardTitle>Improvement Priorities</CardTitle>
                <CardDescription>Focus areas for maximum impact</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {optimizationResult.improvements.map((improvement, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{improvement.parameter}</span>
                        <Badge
                          variant={
                            improvement.priority === 'high' ? 'destructive' :
                            improvement.priority === 'medium' ? 'default' : 'secondary'
                          }
                        >
                          {improvement.priority} priority
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span>Current: {improvement.currentValue.toFixed(1)}</span>
                        <span>→</span>
                        <span className="text-green-600">Optimal: {improvement.optimalValue.toFixed(1)}</span>
                        <span className="ml-auto">
                          {improvement.improvement > 0 ? '+' : ''}{improvement.improvement.toFixed(1)}
                        </span>
                      </div>
                      <Progress 
                        value={(improvement.currentValue / improvement.optimalValue) * 100} 
                        className="h-2"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Optimization Tab */}
        <TabsContent value="optimization" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Optimal Shot Parameters</CardTitle>
              <CardDescription>Personalized targets for {selectedShotType}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Shot Type</Label>
                    <select
                      value={selectedShotType}
                      onChange={(e) => setSelectedShotType(e.target.value)}
                      className="w-full mt-1 p-2 border rounded"
                    >
                      <option value="drive">Drive</option>
                      <option value="pull">Pull</option>
                      <option value="cut">Cut</option>
                      <option value="sweep">Sweep</option>
                      <option value="loft">Loft</option>
                    </select>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Bat Speed</span>
                      <span className="font-mono">{optimalParameters.batSpeed.toFixed(1)} m/s</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Launch Angle</span>
                      <span className="font-mono">{optimalParameters.launchAngle.toFixed(1)}°</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Impact Location</span>
                      <span className="font-mono">Sweet Spot</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Predicted Distance</span>
                      <span className="font-mono">{optimalParameters.predictedDistance.toFixed(0)}m</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label>Body Position</Label>
                    <div className="space-y-2 mt-2">
                      <div className="flex justify-between text-sm">
                        <span>Stance</span>
                        <span>{optimalParameters.bodyPosition.stance}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Back Lift</span>
                        <span>{optimalParameters.bodyPosition.backLift}°</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Follow Through</span>
                        <span>{optimalParameters.bodyPosition.followThrough}°</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Confidence</Label>
                    <Progress value={optimalParameters.confidence * 100} className="mt-2" />
                    <span className="text-sm text-muted-foreground">
                      {(optimalParameters.confidence * 100).toFixed(0)}% confidence
                    </span>
                  </div>
                </div>
              </div>
              
              {optimizationResult && (
                <div className="mt-6 p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">Coaching Recommendations</h4>
                  <ul className="space-y-1">
                    {optimizationResult.recommendations.map((rec, index) => (
                      <li key={index} className="text-sm flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Real-Time Tab */}
        <TabsContent value="realtime" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Real-Time Optimization</CardTitle>
              <CardDescription>Live coaching during shot execution</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Real-time controls */}
                <div className="space-y-4">
                  <div>
                    <Label>Bat Speed: {realTimeBatSpeed.toFixed(1)} m/s</Label>
                    <Slider
                      value={[realTimeBatSpeed]}
                      onValueChange={(v) => setRealTimeBatSpeed(v[0])}
                      min={10}
                      max={50}
                      step={1}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label>Launch Angle: {realTimeAngle.toFixed(1)}°</Label>
                    <Slider
                      value={[realTimeAngle]}
                      onValueChange={(v) => setRealTimeAngle(v[0])}
                      min={-20}
                      max={60}
                      step={1}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label>Time to Impact: {timeToImpact}ms</Label>
                    <Slider
                      value={[timeToImpact]}
                      onValueChange={(v) => setTimeToImpact(v[0])}
                      min={0}
                      max={1000}
                      step={10}
                      className="mt-2"
                    />
                  </div>
                </div>
                
                {/* Real-time feedback */}
                {realTimeOptimization && (
                  <Alert className={cn(
                    realTimeOptimization.urgency === 'high' ? 'border-red-500' :
                    realTimeOptimization.urgency === 'medium' ? 'border-yellow-500' :
                    'border-green-500'
                  )}>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-lg font-medium">
                      {realTimeOptimization.adjustment}
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Visual indicator */}
                <div className="flex justify-center">
                  <div className={cn(
                    'w-32 h-32 rounded-full flex items-center justify-center',
                    realTimeOptimization?.urgency === 'high' ? 'bg-red-100' :
                    realTimeOptimization?.urgency === 'medium' ? 'bg-yellow-100' :
                    'bg-green-100'
                  )}>
                    <Target className={cn(
                      'h-16 w-16',
                      realTimeOptimization?.urgency === 'high' ? 'text-red-500' :
                      realTimeOptimization?.urgency === 'medium' ? 'text-yellow-500' :
                      'text-green-500'
                    )} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};